"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PairStableSwap = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const ISwap_1 = require("../../types/web3-v1-contracts/ISwap");
const ERC20_1 = require("../../types/web3-v1-contracts/ERC20");
const web3_utils_1 = require("web3-utils");
const pair_1 = require("../pair");
const utils_1 = require("../utils");
const mainnet_PairStableSwap_addr_json_1 = require("../../tools/deployed/mainnet.PairStableSwap.addr.json");
const multicall_1 = require("../multicall");
class PairStableSwap extends pair_1.Pair {
    constructor(chainId, web3, swapPoolAddr, lp) {
        super((0, utils_1.selectAddress)(chainId, { mainnet: mainnet_PairStableSwap_addr_json_1.address }));
        this.web3 = web3;
        this.swapPoolAddr = swapPoolAddr;
        this.allowRepeats = false;
        this.paused = false;
        this.tokenPrecisionMultipliers = [];
        this.balancesWithAdjustedPrecision = [];
        this.swapFee = new bignumber_js_1.default(0);
        this.preciseA = new bignumber_js_1.default(0);
        this.decimals = [18, 18];
        this.getY = (x, xp, a) => {
            // See: https://github.com/mobiusAMM/mobiusV1/blob/master/contracts/SwapUtils.sol#L531
            const d = this.getD(xp, a);
            const nTokens = xp.length;
            const nA = a.multipliedBy(nTokens);
            const s = x;
            const c = d
                .multipliedBy(d)
                .div(x.multipliedBy(nTokens))
                .integerValue()
                .multipliedBy(d)
                .multipliedBy(PairStableSwap.A_PRECISION)
                .div(nA.multipliedBy(nTokens))
                .integerValue();
            const b = s
                .plus(d.multipliedBy(PairStableSwap.A_PRECISION).div(nA))
                .integerValue();
            let yPrev;
            let y = d;
            for (let i = 0; i < 256; i++) {
                yPrev = y;
                y = y
                    .multipliedBy(y)
                    .plus(c)
                    .div(y.multipliedBy(2).plus(b).minus(d))
                    .integerValue();
                if (y.minus(yPrev).abs().lte(1)) {
                    return y;
                }
            }
            throw new Error("SwapPool approximation did not converge!");
        };
        this.lpToken = lp;
        this.swapPool = new web3.eth.Contract(ISwap_1.ABI, swapPoolAddr);
    }
    async _init() {
        const [tokenA, tokenB] = await Promise.all([
            this.swapPool.methods.getToken(0).call(),
            this.swapPool.methods.getToken(1).call(),
        ]);
        const erc20A = new this.web3.eth.Contract(ERC20_1.ABI, tokenA);
        const erc20B = new this.web3.eth.Contract(ERC20_1.ABI, tokenB);
        const [decimalsA, decimalsB] = await Promise.all([
            erc20A.methods.decimals().call(),
            erc20B.methods.decimals().call(),
        ]);
        this.decimals = [parseInt(decimalsA), parseInt(decimalsB)];
        this.tokenPrecisionMultipliers = [
            new bignumber_js_1.default(10).pow(PairStableSwap.POOL_PRECISION_DECIMALS - Number.parseInt(decimalsA)),
            new bignumber_js_1.default(10).pow(PairStableSwap.POOL_PRECISION_DECIMALS - Number.parseInt(decimalsB)),
        ];
        return {
            pairKey: this.swapPoolAddr,
            tokenA,
            tokenB,
        };
    }
    get canDepositOneSided() {
        return true;
    }
    async loadLpAddress() {
        return true;
    }
    reflectLiquidityChanges(liquidityChanges) {
        const copy = this.copy();
        copy.balancesWithAdjustedPrecision = copy.balancesWithAdjustedPrecision.map((el, i) => el.plus(liquidityChanges[i].times(this.tokenPrecisionMultipliers[i])));
        return copy;
    }
    async refresh() {
        const [paused, balances, swapFee, preciseA] = await Promise.all([
            this.swapPool.methods.paused().call(),
            this.swapPool.methods.getBalances().call(),
            this.swapPool.methods.getSwapFee().call(),
            this.swapPool.methods.getAPrecise().call(),
        ]);
        if (balances.length !== 2) {
            throw new Error("pool must have only 2 tokens!");
        }
        this.paused = paused;
        this.balancesWithAdjustedPrecision = balances.map((b, idx) => this.tokenPrecisionMultipliers[idx].multipliedBy(b));
        this.swapFee = new bignumber_js_1.default(swapFee).div(new bignumber_js_1.default(10).pow(10));
        this.preciseA = new bignumber_js_1.default(preciseA);
    }
    outputAmount(inputToken, inputAmount) {
        if (this.paused) {
            return new bignumber_js_1.default(0);
        }
        // See: https://github.com/mobiusAMM/mobiusV1/blob/master/contracts/SwapUtils.sol#L617
        const [tokenIndexFrom, tokenIndexTo] = inputToken === this.tokenA ? [0, 1] : [1, 0];
        const x = inputAmount
            .multipliedBy(this.tokenPrecisionMultipliers[tokenIndexFrom])
            .plus(this.balancesWithAdjustedPrecision[tokenIndexFrom]);
        const y = this.getY(x, this.balancesWithAdjustedPrecision, this.preciseA);
        const outputAmountWithFee = this.balancesWithAdjustedPrecision[tokenIndexTo]
            .minus(y)
            .minus(1);
        const fee = outputAmountWithFee.multipliedBy(this.swapFee);
        const outputAmount = outputAmountWithFee
            .minus(fee)
            .div(this.tokenPrecisionMultipliers[tokenIndexTo])
            .integerValue();
        return outputAmount;
    }
    getD(xp, a) {
        // See: https://github.com/mobiusAMM/mobiusV1/blob/master/contracts/SwapUtils.sol#L393
        const s = bignumber_js_1.default.sum(...xp);
        if (s.eq(0)) {
            return s;
        }
        let prevD;
        let d = s;
        const nTokens = xp.length;
        const nA = a.multipliedBy(nTokens);
        for (let i = 0; i < 256; i++) {
            let dP = d;
            xp.forEach((x) => {
                dP = dP.multipliedBy(d).div(x.multipliedBy(nTokens)).integerValue();
            });
            prevD = d;
            d = nA
                .multipliedBy(s)
                .div(PairStableSwap.A_PRECISION)
                .plus(dP.multipliedBy(nTokens))
                .multipliedBy(d)
                .div(nA
                .minus(PairStableSwap.A_PRECISION)
                .multipliedBy(d)
                .div(PairStableSwap.A_PRECISION)
                .plus(new bignumber_js_1.default(nTokens).plus(1).multipliedBy(dP)))
                .integerValue();
            if (d.minus(prevD).abs().lte(1)) {
                return d;
            }
        }
        throw new Error("SwapPool D does not converge!");
    }
    swapExtraData() {
        return this.swapPoolAddr;
    }
    depositExtraData() {
        var _a, _b;
        return ((_b = (0, web3_utils_1.encodePacked)({
            value: this.swapPoolAddr,
            type: "address",
        }, {
            value: (_a = this.gaugeAddress) !== null && _a !== void 0 ? _a : "",
            type: "address",
        })) !== null && _b !== void 0 ? _b : `${this.swapPoolAddr}${this.gaugeAddress}`);
    }
    snapshot() {
        return {
            paused: this.paused,
            tokenPrecisionMultipliers: this.tokenPrecisionMultipliers.map((n) => n.toFixed()),
            balancesWithAdjustedPrecision: this.balancesWithAdjustedPrecision.map((n) => n.toFixed()),
            swapFee: this.swapFee.toFixed(),
            preciseA: this.preciseA.toFixed(),
        };
    }
    // https://github.com/mobiusAMM/mobiusV1/blob/75eb17d390e28c91e640c82d5610191d5852cbb4/contracts/SwapUtils.sol#L866
    depositAmount(amountA, amountB) {
        const amounts = [amountA, amountB];
        const lpSupply = this.lpSupply;
        const newBalances = this.balancesWithAdjustedPrecision.map((el, i) => el.plus(amounts[i].times(this.tokenPrecisionMultipliers[i])));
        const [d0, d1] = [
            this.getD(this.balancesWithAdjustedPrecision, this.preciseA),
            this.getD(newBalances, this.preciseA),
        ];
        return d1.minus(d0).times(lpSupply).div(d0);
    }
    withdrawAmount(lpAmount) {
        const multiplier = lpAmount.dividedBy(this.lpSupply);
        return this.balancesWithAdjustedPrecision.map((bal, i) => bal.multipliedBy(multiplier).div(this.tokenPrecisionMultipliers[i]));
    }
    restore(snapshot) {
        var _a, _b, _c, _d;
        this.paused = snapshot.paused;
        this.swapFee = new bignumber_js_1.default(snapshot.swapFee);
        this.preciseA = new bignumber_js_1.default(snapshot.preciseA);
        this.tokenPrecisionMultipliers =
            (_a = this.tokenPrecisionMultipliers) !== null && _a !== void 0 ? _a : (_b = snapshot.tokenPrecisionMultipliers) === null || _b === void 0 ? void 0 : _b.map((r) => new bignumber_js_1.default(r));
        this.balancesWithAdjustedPrecision =
            (_c = this.balancesWithAdjustedPrecision) !== null && _c !== void 0 ? _c : (_d = snapshot.balancesWithAdjustedPrecision) === null || _d === void 0 ? void 0 : _d.map((r) => new bignumber_js_1.default(r));
    }
    bootstrap({ tokenA, tokenB, pairKey, poolAddress, lpSupply, lpToken, gaugeAddress, ...rest }) {
        var _a, _b;
        this.tokenPrecisionMultipliers =
            (_b = (_a = rest.decimals) === null || _a === void 0 ? void 0 : _a.map((dec) => new bignumber_js_1.default(10).pow(PairStableSwap.POOL_PRECISION_DECIMALS - dec))) !== null && _b !== void 0 ? _b : this.tokenPrecisionMultipliers;
        this.balancesWithAdjustedPrecision = rest.balances.map((b, idx) => this.tokenPrecisionMultipliers[idx].multipliedBy(b));
        this.gaugeAddress = gaugeAddress !== null && gaugeAddress !== void 0 ? gaugeAddress : this.gaugeAddress;
        super.bootstrap({
            tokenA,
            tokenB,
            pairKey,
            poolAddress,
            lpSupply,
            lpToken,
        });
        this.restore(rest);
    }
    getMulticallPayloadForBootstrap() {
        const template = {
            targetInterface: utils_1.StableSwapInterface,
            target: this.swapPoolAddr,
        };
        const callInfo = [
            {
                fieldName: "paused",
                method: "paused",
                transformResult: (r) => !!r[0],
            },
            {
                fieldName: "swapFee",
                method: "getSwapFee",
                transformResult: (r) => new bignumber_js_1.default(r[0].toString())
                    .dividedBy(new bignumber_js_1.default("10").pow(10))
                    .toFixed(),
            },
            {
                fieldName: "balances",
                method: "getBalances",
                transformResult: (r) => r[0].map((el) => el.toString()),
            },
            {
                fieldName: "preciseA",
                method: "getAPrecise",
                transformResult: multicall_1.convertResultToString,
            },
        ];
        return callInfo.map((el) => ({ ...template, ...el }));
    }
    getDescriptor() {
        return {
            ...super.getDescriptor(),
            _type: "stableswap",
            poolAddress: this.swapPoolAddr,
            decimals: this.decimals,
        };
    }
}
exports.PairStableSwap = PairStableSwap;
PairStableSwap.POOL_PRECISION_DECIMALS = 18;
PairStableSwap.A_PRECISION = 100;
//# sourceMappingURL=stableswap.js.map